import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import RouterPropTypes from 'react-router-prop-types';
import { withRouter, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Alert, Button, Row } from 'reactstrap';
import PropTypes from 'prop-types';

import RequestState from '../../../types/RequestState';
import ContentForState from '../../loader/ContentForState';
import OrderStore from '../../../store/OrderStore';
import { modelOf } from '../../../prop-types';
import Analytics from '../../../analytics/Analytics';
import globalTranslations from '../../../i18n/globalTranslations';
import ErrorHandler from '../../loader/ErrorHandler';
import Redirect302 from '../../route/Redirect302';
import * as queryString from '../../../util/queryString';
import HelperNavigation from '../../common/HelperNavigation';
import CustomCol from '../../bootstrap/CustomCol';
import PageTitle from '../../common/PageTitle';
import TrackingPixels from '../../../analytics/TrackingPixels';
import ShopAlike from '../../../analytics/ShopAlike';
import Icon from '../../common/Icon';
import ConfigStore from '../../../store/ConfigStore';
import AdZones from '../../../types/AdZones';
import ProposalStore from '../../../store/ProposalStore';
import OrderType from '../../../types/OrderType';
import Paths from '../../../types/Paths';
import CheckoutConfirmation from '../CheckoutConfirmation';
import AdStore from '../../../store/AdStore';
import ContentSlot from '../../ad/ContentSlot';

const ThankYouPageContent = ({
  adStore,
  configStore,
  orderStore,
  proposalStore,
  analytics,
  intl,
  match,
  location,
  onOrderSuccess,
  orderType,
}) => {
  const [renderTrackingPixels, setRenderTrackingPixels] = useState(false);

  useEffect(() => {
    orderType === OrderType.PROPOSAL_REQUEST
      ? loadRequestSuccess()
      : loadOrder();
  }, []);

  const loadOrder = () => {
    const { orderId, trackingCode } = match.params;
    const queryParams = queryString.parse(location.search);
    const merchantId = queryParams.merchantId;
    const orderParams = {
      orderId,
      trackingCode,
      merchantId,
    };
    orderStore
      .loadCurrentOrderPostPurchase(orderParams)
      .then(() => {
        configStore.gtm.enabled && updateAnalyticsDataLayer();
        sendGdprData();
        sendAnalyticsPurchaseEvent();
        setupAnalyticsTrackingEvent();
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const loadRequestSuccess = () => {
    const { id, trackingCode } = match.params;
    const params = {
      id,
      trackingCode,
    };

    proposalStore
      .loadCurrentOrderSuccess(params)
      .then(() => {
        configStore.gtm.enabled && updateAnalyticsDataLayer();
        sendGdprData();
        sendAnalyticsPurchaseEvent();
        setupAnalyticsTrackingEvent();
      })
      .catch((error) => console.error(error));
  };

  const updateAnalyticsDataLayer = () => {
    const currentOrder =
      orderType === OrderType.PROPOSAL_REQUEST
        ? proposalStore.proposal
        : orderStore.currentOrder;

    // Because the data array is not organized with keys or inside js objects,
    // we have to assume that the correct object is at index 0.
    if (
      currentOrder &&
      currentOrder.shippingMethod &&
      window.dataLayer &&
      window.dataLayer.length > 0
    ) {
      const dimensionKey = 'shippingMethod';
      const dimensionValue = currentOrder.shippingMethod.name;
      analytics.updateDimension(dimensionKey, dimensionValue);
    }

    if (
      currentOrder &&
      currentOrder.paymentMethod &&
      window.dataLayer &&
      window.dataLayer.length > 0
    ) {
      const dimensionKey = 'paymentMethod';
      const dimensionValue = currentOrder.paymentMethod.name;
      analytics.updateDimension(dimensionKey, dimensionValue);
    }
  };

  const sendGdprData = () => {
    const currentOrder =
      orderType === OrderType.PROPOSAL_REQUEST
        ? proposalStore.proposal
        : orderStore.currentOrder;

    if (currentOrder.is_success_seen || !currentOrder.gdpr_info) {
      return;
    }

    analytics.sendGdprData(currentOrder.gdpr_info);
  };

  const sendAnalyticsPurchaseEvent = () => {
    const isProposal = orderType === OrderType.PROPOSAL_REQUEST;

    const currentOrder = isProposal
      ? proposalStore.proposal
      : orderStore.currentOrder;

    setRenderTrackingPixels(!currentOrder.is_success_seen);

    if (currentOrder.is_success_seen) {
      return;
    }

    if (isProposal) {
      analytics.proposalSent(currentOrder);
    } else {
      analytics.purchase(currentOrder);
    }
  };

  const setupAnalyticsTrackingEvent = () => {
    const currentOrder =
      orderType === OrderType.PROPOSAL_REQUEST
        ? proposalStore.proposal
        : orderStore.currentOrder;

    onOrderSuccess && currentOrder && onOrderSuccess(currentOrder);
  };

  const getOrderRequestState = () => {
    return orderType === OrderType.PROPOSAL_REQUEST
      ? proposalStore.successState
      : orderStore.currentOrderState;
  };

  const getCurrentlyRunningRequestState = () => {
    const currentRequestState = [getOrderRequestState()].find(
      (state) => state !== RequestState.LOADED
    );

    return currentRequestState || RequestState.LOADED;
  };

  const getBreadcrumbsPath = () => {
    if (orderType === OrderType.PROPOSAL_REQUEST) {
      return [
        {
          text: intl.formatMessage(globalTranslations.checkoutTitle),
        },
        {
          text: intl.formatMessage(
            globalTranslations.successfulProposalBreadcrumbName
          ),
        },
      ];
    }

    return [
      {
        text: intl.formatMessage(globalTranslations.checkoutTitle),
      },
      {
        text: intl.formatMessage(
          globalTranslations.successfulPurchaseBreadcrumbName
        ),
      },
    ];
  };

  const getError = () => {
    const lastError =
      orderType === OrderType.PROPOSAL_REQUEST
        ? proposalStore.lastCurrentOrderError
        : orderStore.lastCurrentOrderError;

    const error = lastError;
    return error || null;
  };

  const renderHtmlWidget = (widgetHtml) => {
    const doc = document.implementation.createHTMLDocument('');
    doc.body.innerHTML = widgetHtml;

    return (
      <>
        <Helmet>
          {[].map.call(doc.getElementsByTagName('script'), (el) => (
            <script>{el.textContent}</script>
          ))}
        </Helmet>
        <div dangerouslySetInnerHTML={{ __html: widgetHtml }} />
      </>
    );
  };

  const renderErrorHandler = (props) => {
    const { checkout } = configStore;
    switch (props.error.httpStatusCode) {
      case 401:
        return <Redirect302 to="/" />;
      case 404:
        const path =
          orderType === OrderType.PROPOSAL_REQUEST
            ? Paths.ProposalCheckout
            : checkout.checkoutPath;
        return <Redirect302 to={path} />;
      default:
        return <ErrorHandler {...props} />;
    }
  };

  const getContent = () => {
    const currentOrder =
      orderType === OrderType.PROPOSAL_REQUEST
        ? proposalStore.proposal
        : orderStore.currentOrder;
    const paymentMethodMessages = currentOrder.payment_method_messages;

    return currentOrder.html_widget ? (
      <CustomCol xs={12} md={10} xxl={7}>
        <div className="ThankYouPageContent__payment-method-messages">
          {paymentMethodMessages &&
            paymentMethodMessages.length > 0 &&
            paymentMethodMessages.map((message, index) => (
              <Alert key={index} color="warning">
                <span>{message}</span>
              </Alert>
            ))}
        </div>
        {renderHtmlWidget(currentOrder.html_widget)}
      </CustomCol>
    ) : (
      <CheckoutConfirmation currentOrder={currentOrder} orderType={orderType} />
    );
  };

  const renderBannerContent = () => {
    const ads = adStore.getGlobalAdsByAdZone(
      AdZones.ORDER_CONFIRMATION_CONTENT
    );

    if (!ads || ads.length === 0) {
      return null;
    }

    return (
      <div className="ThankYouPageContent__banner-container">
        {ads.map((ad) => (
          <ContentSlot
            ad={ad}
            bannerZone={AdZones.ORDER_CONFIRMATION_CONTENT}
            key={ad.id}
          />
        ))}
      </div>
    );
  };

  const renderReceiptUrl = () => {
    const currentOrder =
      orderType === OrderType.PROPOSAL_REQUEST
        ? proposalStore.proposal
        : orderStore.currentOrder;

    return (
      <Button
        tag="a"
        href={currentOrder.receipt_url}
        target="_blank"
        rel="nofollow noopener"
        color="primary"
        className="ThankYouPageContent__receipt-button"
      >
        <Icon name="print" />{' '}
        <FormattedMessage
          id="checkout.successfulPurchaseReceipt"
          defaultMessage="Receipt"
        />
      </Button>
    );
  };

  const renderSuccessPage = () => {
    const currentOrder =
      orderType === OrderType.PROPOSAL_REQUEST
        ? proposalStore.proposal
        : orderStore.currentOrder;

    const title =
      orderType === OrderType.PURCHASE ? (
        <FormattedMessage
          id="checkout.successfulPurchasePageHeading"
          defaultMessage="Order confirmation"
        />
      ) : (
        <FormattedMessage
          id="checkout.successfulProposalPageHeading"
          defaultMessage="Proposal request confirmation"
        />
      );

    return (
      <>
        <HelperNavigation breadcrumbsPath={getBreadcrumbsPath()} />
        <Row className="ThankYouPageContent">
          <CustomCol
            xs={12}
            className="ThankYouPageContent__order-confirmation"
          >
            <PageTitle>{title}</PageTitle>
          </CustomCol>
          {getContent()}
          <CustomCol xs={12} md={10} xxl={7}>
            {currentOrder.receipt_url && renderReceiptUrl()}
            <div className="ThankYouPageContent__back-to-store-button">
              <Link to="/">
                <Button color="secondary">
                  <FormattedMessage {...globalTranslations.backToStore} />
                </Button>
              </Link>
            </div>
            {renderBannerContent()}
          </CustomCol>
        </Row>
        {renderTrackingPixels && <TrackingPixels />}
        {renderTrackingPixels && <ShopAlike />}
      </>
    );
  };

  return (
    <ContentForState
      state={getCurrentlyRunningRequestState()}
      forLoaded={renderSuccessPage}
      error={getError()}
      errorHandler={renderErrorHandler}
    />
  );
};

ThankYouPageContent.propTypes = {
  adStore: modelOf(AdStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  orderStore: modelOf(OrderStore).isRequired,
  proposalStore: modelOf(ProposalStore).isRequired,
  analytics: PropTypes.instanceOf(Analytics).isRequired,
  intl: intlShape.isRequired,
  match: RouterPropTypes.match.isRequired,
  location: RouterPropTypes.location.isRequired,
  onOrderSuccess: PropTypes.func,
  orderType: PropTypes.string,
};

export default injectIntl(
  inject(
    'adStore',
    'configStore',
    'currencyStore',
    'orderStore',
    'proposalStore',
    'analytics'
  )(withRouter(observer(ThankYouPageContent)))
);
